import { Tooltip } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

const isTouchDevice = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;

let tooltipCount = 0;

/**
 * TooltipCustom - Accessible tooltip following WAI-ARIA tooltip pattern.
 *
 * @param {string}  aria       - 'describedby' (extra info) | 'labelledby' (primary label, e.g. icon-only buttons)
 * @param {bool}    arrow      - Show arrow pointer
 * @param {node}    children   - Trigger element (must support ref forwarding)
 * @param {string}  placement  - Tooltip position (top | bottom | left | right)
 * @param {node}    title      - Tooltip content
 */
function TooltipCustom({ aria, arrow, children, classes, disabled, leaveDelay, placement, title }) {
  const [isOpen, setIsOpen] = useState(false);

  const idRef = useRef(null);
  if (idRef.current === null) {
    tooltipCount += 1;
    idRef.current = `tooltip-${tooltipCount}`;
  }
  const id = idRef.current;

  const isLabelledBy = aria === 'labelledby';

  // For 'labelledby': add aria-labelledby to the child (does not conflict with MUI's aria-describedby)
  // For 'describedby' (default): let MUI handle aria-describedby natively — no cloneElement needed
  const child = (!disabled && title && isLabelledBy)
    ? React.cloneElement(children, { 'aria-labelledby': id })
    : children;

  // role="tooltip" for WCAG SC 4.1.2 (MUI v4 does not add it)
  // id only in labelledby mode so aria-labelledby can reference the tooltip content
  const tooltipTitle = (disabled || !title)
    ? ''
    : isLabelledBy
      ? <span id={id} role="tooltip">{title}</span>
      : <span role="tooltip">{title}</span>;

  useEffect(() => {
    if (!isOpen) return;
    function handleEscape(e) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  function handleOpen() {
    setIsOpen(true);
  }

  function handleClose() {
    setIsOpen(false);
  }

  return (
    <Tooltip
      arrow={arrow}
      classes={classes}
      disableHoverListener={isTouchDevice}
      disableTouchListener
      interactive
      leaveDelay={leaveDelay}
      onClose={handleClose}
      onOpen={handleOpen}
      open={isOpen}
      placement={placement}
      // keepMounted keeps the node in DOM for aria-labelledby; pointer-events:none when closed prevents the mounted Popper from intercepting hover/clicks on elements behind it (e.g. back button on narrow viewports)
      PopperProps={{ keepMounted: isLabelledBy, style: !isOpen ? { pointerEvents: 'none' } : undefined }}
      title={tooltipTitle}
    >
      {child}
    </Tooltip>
  );
}

TooltipCustom.propTypes = {
  aria: PropTypes.oneOf(['describedby', 'labelledby']),
  arrow: PropTypes.bool,
  children: PropTypes.element.isRequired,
  classes: PropTypes.object,
  disabled: PropTypes.bool,
  leaveDelay: PropTypes.number,
  placement: PropTypes.string,
  title: PropTypes.node.isRequired,
};

TooltipCustom.defaultProps = {
  aria: 'describedby',
  arrow: false,
  classes: {},
  disabled: false,
  leaveDelay: 0,
  placement: 'top',
};

export default TooltipCustom;
